import React, { useState, useMemo, useCallback, useContext } from 'react';
import classNames from 'classnames';
import { makeStyles } from '@material-ui/core/styles';
import MappingsContext from 'helpers/MappingsContext';

import Popper from '@material-ui/core/Popper';
import Grow from '@material-ui/core/Grow';
import Paper from '@material-ui/core/Paper';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import Tooltip from '@material-ui/core/Tooltip';
import AttributeMenu from './AttributeMenu';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import CreateAttributeDialog from 'components/Mappings/CreateAttributeDialog';

import * as MappingTypes from 'types/Mapping';

interface Props {
  placeholderText?: string;
  onChange: (value: any) => void;
  value: string;
  isSourceCollapsed?: boolean;
  isCsvSource?: boolean;
  sourceAttrOptions: any;
  isDisabled?: boolean;
}

function SourceSelector({
  placeholderText: _placeholderText,
  onChange,
  value,
  isSourceCollapsed = false,
  isCsvSource,
  sourceAttrOptions,
  isDisabled,
}: Props) {
  const classes = useStyles();
  const selectedAttributeDisplayName = isSourceCollapsed ? value.split('.')[0] : value;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const placeholderProperty = isCsvSource ? 'header' : 'property';
  const placeholderText = _placeholderText || `Assign a source ${placeholderProperty}`;
  const displayName = selectedAttributeDisplayName || placeholderText;
  const mappingsContext: MappingTypes.MappingContext = useContext(MappingsContext);
  const { onAddCustomSourceAttribute } = mappingsContext;

  const handleToggle = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : e.currentTarget);
  };

  const handleClickAway = () => {
    setAnchorEl(null);
  };

  function handleSelectAttribute(newAttribute: any) {
    onChange(newAttribute);
    setAnchorEl(null);
  }

  const [search, setSearch] = useState('');
  const [newPropValue, setNewPropValue] = useState<string | null>(null);

  const menuItems = useMemo(() => {
    const list = sourceAttrOptions.filter((attribute: any[]) => {
      return attribute.includes(search.toLowerCase());
    });

    return list;
  }, [search, sourceAttrOptions]);

  const handleSearch = useCallback(e => setSearch(e.target.value), [setSearch]);

  const handleUpdateCustomProp = (value: any) => {
    setNewPropValue(value);
  };

  const handleSaveCustomProps = () => {
    !!newPropValue && onAddCustomSourceAttribute(newPropValue);
    handleSelectAttribute(newPropValue);
  };

  return (
    <>
      <Tooltip title={displayName !== placeholderText ? displayName : ''} placement="top" arrow>
        <div onClick={handleToggle} className={classes.displayNameContainer}>
          <div className={classes.displayName}>{displayName}</div>

          <ArrowDropDownIcon
            className={classNames(classes.dropdownIcon, isDisabled && classes.disabled)}
          />
        </div>
      </Tooltip>

      <Popper
        open={!!anchorEl}
        anchorEl={anchorEl}
        transition
        disablePortal
        className={classes.popperRoot}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <ClickAwayListener onClickAway={handleClickAway}>
              <Paper elevation={2} className={classes.paperRoot}>
                <AttributeMenu
                  onSelectAttribute={handleSelectAttribute}
                  search={search}
                  newPropValue={newPropValue}
                  menuItems={menuItems}
                  onSearch={handleSearch}
                  onSetNewProp={handleUpdateCustomProp}
                  onSaveCustomProps={handleSaveCustomProps}
                />
              </Paper>
            </ClickAwayListener>
          </Grow>
        )}
      </Popper>
      {!!anchorEl && <CreateAttributeDialog onCreate={handleSelectAttribute} />}
    </>
  );
}

const useStyles = makeStyles(theme => ({
  root: {
    '& .MuiTextField-root': {
      margin: theme.spacing(1),
      width: '25ch',
    },
  },
  displayNameContainer: {
    height: 38,
    padding: theme.spacing(1),
    fontSize: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    transition: 'backgroundColor .4s',
    backgroundColor: theme.palette.common.white,
    borderBottom: `1px solid ${theme.palette.text.secondary}`,
    '&:hover': {
      backgroundColor: theme.palette.grey[100],
    },
  },
  displayName: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  hasError: {
    borderBottom: `2px solid ${theme.palette.error.main}`,
  },
  dropdownIcon: {
    color: theme.palette.text.secondary,
  },

  popperRoot: {
    // @ts-ignore
    zIndex: theme.zIndex.surface,
  },
  paperRoot: {
    overflow: 'hidden',
  },
  disabled: {
    color: theme.palette.text.disabled,
  },
}));

export default SourceSelector;
